import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface PaginationProps {
  currentPage: number
  totalPages: number
  onPageChange: (page: number) => void
  /** Prefix for every data-testid, so two pagers can be told apart. */
  testIdPrefix?: string
  /** Spacing above the control; the dashboard's grid wants more than a modal. */
  className?: string
}

// Build the list of "slots" shown in the control: numbered pages and at most
// two ellipsis markers. Always include first + last + the window around the
// current page so users can see where they are without scrolling through
// hundreds of buttons.
function buildPageSlots(current: number, total: number): Array<number | 'ellipsis-left' | 'ellipsis-right'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const slots: Array<number | 'ellipsis-left' | 'ellipsis-right'> = [1]
  const windowStart = Math.max(2, current - 1)
  const windowEnd = Math.min(total - 1, current + 1)

  if (windowStart > 2) slots.push('ellipsis-left')
  for (let p = windowStart; p <= windowEnd; p++) slots.push(p)
  if (windowEnd < total - 1) slots.push('ellipsis-right')

  slots.push(total)
  return slots
}

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  testIdPrefix = 'dashboard-pagination',
  className = 'mt-8',
}: PaginationProps) {
  const { t } = useTranslation('settings')
  const slots = useMemo(() => buildPageSlots(currentPage, totalPages), [currentPage, totalPages])

  if (totalPages <= 1) return null

  const goTo = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return
    onPageChange(page)
  }

  const btnBase =
    'min-w-[2rem] h-8 px-2 rounded-md text-sm border border-border transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer'

  return (
    <nav
      role="navigation"
      aria-label={t('pagination.ariaLabel')}
      className={`flex items-center justify-center gap-1.5 ${className}`}
      data-testid={testIdPrefix}
    >
      <button
        type="button"
        onClick={() => goTo(currentPage - 1)}
        disabled={currentPage <= 1}
        className={`${btnBase} bg-panel hover:border-primary/50`}
        data-testid={`${testIdPrefix}-prev`}
        aria-label={t('pagination.previousPage')}
      >
        {t('pagination.prev')}
      </button>
      {slots.map((slot, i) => {
        if (slot === 'ellipsis-left' || slot === 'ellipsis-right') {
          return (
            <span
              key={`${slot}-${i}`}
              className="px-1 text-text-muted text-sm select-none"
              aria-hidden="true"
            >
              …
            </span>
          )
        }
        const isActive = slot === currentPage
        return (
          <button
            key={slot}
            type="button"
            onClick={() => goTo(slot)}
            disabled={isActive}
            aria-current={isActive ? 'page' : undefined}
            aria-label={t('pagination.page', { page: slot })}
            className={`${btnBase} ${
              isActive
                ? 'bg-primary text-white border-primary cursor-default'
                : 'bg-panel hover:border-primary/50'
            }`}
            data-testid={`${testIdPrefix}-page-${slot}`}
          >
            {slot}
          </button>
        )
      })}
      <button
        type="button"
        onClick={() => goTo(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className={`${btnBase} bg-panel hover:border-primary/50`}
        data-testid={`${testIdPrefix}-next`}
        aria-label={t('pagination.nextPage')}
      >
        {t('pagination.next')}
      </button>
    </nav>
  )
}
